// /src/components/private/funding/useFundingSocket.js

import { useCallback, useEffect, useRef } from 'react';
import { fundingSocket } from '../../../../utils/Socket';
import { resumeAll, registerSocket } from '../../../wsRecovery/wsResume';
import { startHeartbeat, stopHeartbeat } from '../../../wsRecovery/heartbeat';
import {
  extractFundingStatsFromWsPayload,
  fetchFundingPanelBundle,
  fundingLastSeen,
  getFundingDiff,
} from './fundingApi';

const RESUME_REGISTRY_KEY = 'funding';

export default function useFundingSocket({
  enabled,
  isLoggedIn,
  onFundingRefetch,
  onFundingStatsUpdate,
}) {
  const lastResumeIdRef = useRef(0);

  const syncLastSeen = useCallback(async (wsLogId) => {
    const seq = Number(wsLogId);
    if (!Number.isFinite(seq) || seq <= 0) return;
    await fundingLastSeen(seq).catch(() => {});
    lastResumeIdRef.current = Math.max(lastResumeIdRef.current, seq);
  }, []);

  const refetchAndSyncPointer = useCallback(async () => {
    const bundle = await fetchFundingPanelBundle();
    if (typeof onFundingRefetch === 'function') {
      onFundingRefetch(bundle.panel);
    }
    const maxSeq = Number(bundle.fundingWsMaxSeq || 0);
    if (maxSeq > 0) {
      await syncLastSeen(maxSeq);
    }
  }, [onFundingRefetch, syncLastSeen]);

  const applyWsPayload = useCallback(async (payload) => {
    const stats = extractFundingStatsFromWsPayload(payload);
    if (stats && typeof onFundingStatsUpdate === 'function') {
      onFundingStatsUpdate(stats);
    } else {
      await refetchAndSyncPointer();
      return;
    }

    if (stats.wsLogId > 0) {
      await syncLastSeen(stats.wsLogId);
    }
  }, [onFundingStatsUpdate, refetchAndSyncPointer, syncLastSeen]);

  useEffect(() => {
    registerSocket(RESUME_REGISTRY_KEY, () => {
      if (!isLoggedIn || !enabled) return;
      if (!fundingSocket.connected) {
        fundingSocket.connect();
        startHeartbeat(fundingSocket);
      }
      refetchAndSyncPointer().catch((err) => {
        console.error('[Funding] resume refetch failed:', err);
      });
    });
  }, [enabled, isLoggedIn, refetchAndSyncPointer]);

  useEffect(() => {
    if (!isLoggedIn || !enabled) return;

    if (!fundingSocket.connected) {
      fundingSocket.connect();
      startHeartbeat(fundingSocket);
    }

    const onConnect = () => {
      fundingSocket.emit('joinFunding');
      resumeAll();
    };

    const onReconnect = async () => {
      fundingSocket.emit('joinFunding');
      try {
        const diff = await getFundingDiff();
        if (diff?.needRefresh) {
          await refetchAndSyncPointer();
        } else if (diff?.maxWsLogSeq) {
          lastResumeIdRef.current = Math.max(
            lastResumeIdRef.current,
            Number(diff.maxWsLogSeq),
          );
        }
      } catch (err) {
        console.error('[Funding] reconnect diff failed:', err);
      }
    };

    fundingSocket.on('connect', onConnect);
    fundingSocket.on('reconnect', onReconnect);

    return () => {
      stopHeartbeat(fundingSocket);
      fundingSocket.off('connect', onConnect);
      fundingSocket.off('reconnect', onReconnect);
    };
  }, [isLoggedIn, enabled, refetchAndSyncPointer]);

  useEffect(() => {
    const handler = () => {
      if (!isLoggedIn || !enabled) return;
      if (!fundingSocket.connected) {
        fundingSocket.connect();
        startHeartbeat(fundingSocket);
      }
    };
    window.addEventListener('auth-ws-refreshed', handler);
    return () => window.removeEventListener('auth-ws-refreshed', handler);
  }, [isLoggedIn, enabled]);

  useEffect(() => {
    if (!isLoggedIn || !enabled) return;

    const resyncIfStale = async () => {
      try {
        const diff = await getFundingDiff();
        if (diff?.needRefresh) {
          await refetchAndSyncPointer();
        }
      } catch (err) {
        console.error('[Funding] visibility/online diff failed:', err);
      }
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      resyncIfStale();
    };

    const onOnline = () => {
      resyncIfStale();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [isLoggedIn, enabled, refetchAndSyncPointer]);

  useEffect(() => {
    if (!isLoggedIn || !enabled) return;

    if (!fundingSocket.connected) {
      fundingSocket.connect();
      startHeartbeat(fundingSocket);
    }

    fundingSocket.emit('joinFunding');

    const onMissed = async () => {
      try {
        await refetchAndSyncPointer();
      } catch (err) {
        console.error('[Funding] fundingMissed refetch failed:', err);
      }
    };

    const onUpdate = async (payload) => {
      try {
        await applyWsPayload(payload);
      } catch (err) {
        console.error('[Funding] fundingUpdate apply failed:', err);
      }
    };

    fundingSocket.on('fundingMissed', onMissed);
    fundingSocket.on('fundingUpdate', onUpdate);

    return () => {
      fundingSocket.off('fundingMissed', onMissed);
      fundingSocket.off('fundingUpdate', onUpdate);
      fundingSocket.emit('leaveFunding');
    };
  }, [isLoggedIn, enabled, refetchAndSyncPointer, applyWsPayload]);

  return { lastResumeIdRef };
}
